import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import lunaBg from "@/assets/luna-bg.jpg";
import BootSequence from "@/components/BootSequence";
import Sidebar from "@/components/Sidebar";
import FriendPanel from "@/components/FriendPanel";
import ImageGalleryPanel from "@/components/ImageGalleryPanel";
import ConversationsPanel from "@/components/ConversationsPanel";
import DocGeneratorPanel from "@/components/DocGeneratorPanel";
import PremiumPanel from "@/components/PremiumPanel";
import VoiceCallModal from "@/components/VoiceCallModal";
import TermsGate from "@/components/TermsGate";
import ReportDialog from "@/components/ReportDialog";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useVoiceCall } from "@/hooks/useVoiceCall";
import { useReminderAlerts } from "@/hooks/useReminderAlerts";
import { useTermsRequired } from "@/hooks/useTermsRequired";
import { usePendingTurn } from "@/hooks/usePendingTurn";
import { dayKey } from "@/lib/dates";
import PanelSlot, { lazyPanel } from "@/components/panel/PanelSlot";
import {
  randomId, sendStreamedTurn, streamingReply, savePendingTurn, clearPendingTurn, giveUpOutcome, sameView, turnView,
  addAnswers, mergeTurnAnswer, viewThread,
} from "@/lib/streamedTurn";
import {
  useRunningGenerations, runningGenerations, newGeneration, runGeneration, pickUpKept, holdPageOnLeave,
  generationDeadline, LEAVE_PROMPT,
} from "@/lib/generations";
import { serverDetail, isTermsRequired, DETAIL_STATUSES } from "@/lib/errorText";
import {
  initialUiLang, initialReplyLang, seedReplyLang, makeT, uiLang, accountLanguage, justCreatedHere,
} from "@/lib/i18n";
import { useFeatures } from "@/lib/features";
import { pendingKind, makingGroup } from "@/lib/chatIntent";
import { downloadName, staleObjectUrls } from "@/lib/files";
import { replyExtras } from "@/lib/turnExtras";
import { serverMessageId, findInHistory } from "@/lib/report";
import {
  sendMedia, sendMediaBatch, clearMessages, fetchMessages, clearMemories, fetchProfile,
  updateProfile, getReplyLang, setReplyLang, turnResult, reportMessage, GENERATE_ROUTES,
} from "@/lib/api";

// Sidebar panels load on demand — see components/panel/PanelSlot.jsx.
const MemoryPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/MemoryPanel"));
const MoodPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/MoodPanel"));
const DayInfoPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/DayInfoPanel"));
const UsagePanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/UsagePanel"));
const SettingsPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/SettingsPanel"));
const NotesPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/NotesPanel"));
const GoalsPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/GoalsPanel"));
const RemindersPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/RemindersPanel"));
const JournalPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/JournalPanel"));
const HobbiesPanel = lazyPanel(() => import(/* webpackPrefetch: true */ "@/components/HobbiesPanel"));

// Wand-menu doc kinds (everything except "image", which already has its own
// imageUrl reply) — one shared handler below drives all of them straight
// from the chat composer instead of the old modal form.
// Minimum time the "Görsel çiziliyor..." state stays visible before the
// image reply appears, even if the backend already responded sooner.
const MIN_IMAGE_GEN_MS = 10000;

// (route: the LunaWorks request — lib/api.js GENERATE_ROUTES; making: what
// the waiting bubble says is being made — FriendPanel)
const DOC_GEN_CONFIG = {
  pdf: { route: GENERATE_ROUTES.pdf, ext: "pdf", making: "pdf" },
  word: { route: GENERATE_ROUTES.word, ext: "docx", making: "docx" },
  ppt: { route: GENERATE_ROUTES.ppt, ext: "pptx", making: "pptx" },
  excel: { route: GENERATE_ROUTES.excel, ext: "xlsx", making: "xlsx" },
  table: { route: GENERATE_ROUTES.table, ext: "png", making: "tableimg" },
  chart: { route: GENERATE_ROUTES.chart, ext: "png", making: "chart" },
};
// A file's extension when its type says none, by its route.
const ROUTE_EXT = Object.fromEntries(Object.values(DOC_GEN_CONFIG).map((c) => [c.route, c.ext]));

// What a LunaWorks generation makes (lib/generations.js entry): "image", or
// its file's kind ("pptx", "xlsx"... — "doc" when unknown).
function workKind(entry) {
  if (!entry) return null;
  return entry.route === GENERATE_ROUTES.image ? "image" : entry.making || "doc";
}

// A generation's own bubble, in the shape of a typed turn's: put back with
// her reply when the screen lacks it (a reload, a view switched), never
// twice (lib/streamedTurn.js mergeTurnAnswer).
const workTurn = (entry) => ({ key: entry.key, text: entry.text, at: entry.startedAt, mode: entry.mode });

// \w is ASCII-only in JS regex — a plain [^\w\s-] strip would eat every
// Turkish letter (ç/ğ/ı/ö/ş/ü) out of the filename. \p{L}\p{N} (with the /u
// flag) keeps any language's letters/digits and only strips real
// filesystem-unsafe characters.
const fileTitle = (title) => (title || "luna-dosya").replace(/[^\p{L}\p{N}\s-]/gu, "").trim() || "luna-dosya";

// LunaWorks' answers worth showing as they come (lib/errorText.js), plus
// the file engine's own "couldn't build it this time" (500 file_failed),
// "couldn't convert it" (502) and "very busy" (503): their sentence, not a
// generic "küçük bir sorun".
const GENERATE_DETAIL_STATUSES = [...DETAIL_STATUSES, 500, 502, 503];

// "image" | "doc" | null: what a reply's attachments are (lib/api.js
// normalizeAttachments) — for the toast of a reply that finished after its
// chat was left.
function attachmentsKind(list) {
  if (!Array.isArray(list) || !list.length) return null;
  return list.some((a) => a?.type === "image") ? "image" : "doc";
}

// How many replies that finished while their view was closed are kept for
// it (Luna's `unseen`), the oldest let go first.
const UNSEEN_MAX = 6;

// Luna greets in the language she answers in (Settings' reply language, or
// the interface's): a German visitor reads English screens and a German hello.
const WELCOME_LINES = {
  friend: {
    tr: (name) => `Merhaba${name ? ` ${name}` : ""}! 🌙 Bugün nasılsın, neler konuşalım?`,
    en: (name) => `Hi${name ? ` ${name}` : ""}! 🌙 How are you today — what shall we talk about?`,
    es: (name) => `¡Hola${name ? ` ${name}` : ""}! 🌙 ¿Cómo estás hoy? ¿De qué hablamos?`,
    de: (name) => `Hallo${name ? ` ${name}` : ""}! 🌙 Wie geht's dir heute — worüber wollen wir reden?`,
    ru: (name) => `Привет${name ? `, ${name}` : ""}! 🌙 Как ты сегодня? О чём поговорим?`,
    it: (name) => `Ciao${name ? ` ${name}` : ""}! 🌙 Come stai oggi? Di cosa parliamo?`,
  },
  work: {
    tr: (name) => `Hoş geldin${name ? `, ${name}` : ""}! 🌙 LunaWorks hazır — görsel, PDF, Excel, Word, ne istersen üretmeye başlayalım.`,
    en: (name) => `Welcome${name ? `, ${name}` : ""}! 🌙 LunaWorks is ready — let's create an image, PDF, Excel, Word, whatever you need.`,
    es: (name) => `¡Hola${name ? ` ${name}` : ""}! 🌙 LunaWorks está listo: una imagen, un PDF, un Excel, un Word, lo que necesites.`,
    de: (name) => `Willkommen${name ? `, ${name}` : ""}! 🌙 LunaWorks ist bereit — Bild, PDF, Excel, Word, was immer du brauchst.`,
    ru: (name) => `Добро пожаловать${name ? `, ${name}` : ""}! 🌙 LunaWorks готов — изображение, PDF, Excel, Word, всё, что нужно.`,
    it: (name) => `Ciao${name ? ` ${name}` : ""}! 🌙 LunaWorks è pronto: un'immagine, un PDF, un Excel, un Word, tutto ciò che ti serve.`,
  },
};

// A turn spoken in the voice call: the call (hooks/useVoiceCall.js, reached
// through callRef) starts saying her first sentence while the rest is still
// being written. gen: the call the turn belongs to; signal: the call lets
// the turn go (hung up, or you cut her off mid-stream).
function sendSpokenTurn(args, callRef, gen, turnId, signal) {
  let started = false;
  return sendStreamedTurn(args, {
    voice: true,
    idempotencyKey: `voice-${turnId || randomId()}`,
    signal,
    onDelta: (text) => {
      if (!started) {
        started = true;
        callRef.current?.onReplyStart(gen, turnId);
      }
      callRef.current?.onReplyDelta(text, gen);
    },
  });
}

function buildWelcomeMessage(mode, lang, name) {
  const key = mode === "work" ? "work" : "friend";
  const lines = WELCOME_LINES[key];
  // (the browser's language counts before it is kept as the reply language)
  const text = (lines[getReplyLang() || initialReplyLang(lang) || lang] || lines[uiLang(lang)])(name);
  return { id: "welcome-" + Date.now(), role: "luna", text, mode };
}

export default function Luna() {
  const [booting, setBooting] = useState(() => {
    try {
      return !localStorage.getItem("luna_booted");
    } catch {
      return false;
    }
  });
  const [mode, setMode] = useState(() => {
    // "jarvis" is a stale value from before L.U.N.A. Modu was removed —
    // never let an old localStorage entry land a user on a mode that no
    // longer renders anything.
    const saved = localStorage.getItem("luna_mode");
    return saved === "jarvis" ? "friend" : saved || "friend";
  });
  // The interface language, always "tr" or "en" (lib/i18n.js): the saved
  // one, else the browser's — a first visit from abroad is in English, not
  // Turkish. Every panel gets this, so none can fall back to Turkish for a
  // "de". The reply language (Settings; lib/api.js getReplyLang) is apart:
  // replySeed is the browser's own language when Luna speaks it (read now,
  // before luna_lang is rewritten), kept below unless one is chosen already.
  const [lang, setLang] = useState(() => initialUiLang());
  const [replySeed] = useState(() => initialReplyLang(initialUiLang()));
  // Ruh Halim: only a mood picked TODAY counts (it's sent with each message
  // so Luna can take it into account); yesterday's pick expires on its own.
  const [mood, setMood] = useState(() => {
    try {
      return localStorage.getItem("luna_mood_day") === dayKey() ? localStorage.getItem("luna_mood") : null;
    } catch {
      return null;
    }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [memoriesOpen, setMemoriesOpen] = useState(false);
  const [openPanel, setOpenPanel] = useState(null); // "journal" | "goals" | "notes" | "alarms" | "mood" | "day-info" | "usage" | null
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [conversationsOpen, setConversationsOpen] = useState(false);
  // null = the default, single ever-growing thread for this mode (unchanged
  // behavior from before Sohbetler existed — just a fresh greeting each
  // visit). A real id means a specific past/new chat is open — its actual
  // transcript loads instead (see the mode/conversationId effect below).
  const [conversationId, setConversationId] = useState(null);
  const [premiumOpen, setPremiumOpen] = useState(false);
  // The profile, kept only while its terms still need (re-)accepting — see
  // the mount effect below; null = nothing to accept.
  const [termsProfile, setTermsProfile] = useState(null);
  // The message of Luna's whose report dialog is open (lib/report.js), or null.
  const [reportTarget, setReportTarget] = useState(null);
  const [userName, setUserName] = useState("");
  const t = makeT(lang);
  // Which abilities the server has on (GET /api/features, lib/features.js).
  const features = useFeatures();
  // What the turn being answered most likely makes ("doc" | "image" | null,
  // lib/chatIntent.js): the waiting bubble then says so instead of showing
  // three dots for the minute a researched deck can take. Once the server
  // says what it makes (the stream's assistant_status: "pptx", "xlsx",
  // "image"...), that — and how long it usually takes (etaS) — instead; a
  // LunaWorks wand request sets its own kind.
  const [making, setMaking] = useState(null);
  const [etaS, setEtaS] = useState(null);
  const [agentStep, setAgentStep] = useState(null); // (new engine: what the model is doing now)
  // The connection went while the server is still answering the typed turn
  // (lib/streamedTurn.js pollTurn heard 409): the waiting bubble says it is
  // still being made and to keep the page open.
  const [stillWorking, setStillWorking] = useState(false);
  // The turn's requests get no answer at all (lib/streamedTurn.js pollTurn
  // onConnection), or the browser says it is offline: the status line says
  // it waits for the connection instead of "Düşünüyorum...".
  const [connected, setConnected] = useState(true);
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  // When the turn being answered was sent (the waiting bubble counts from
  // then — a turn picked up after a reload too), and the view — mode and
  // Sohbet — it belongs to: its waiting bubble shows only there, and its
  // reply joins the screen only there (otherwise a toast says where it is).
  const [turnStartedAt, setTurnStartedAt] = useState(null);
  const [busyView, setBusyView] = useState(null);
  // The LunaWorks generations this page waits on (lib/generations.js): the
  // composer wand's — and the Ürettiklerim panels' once picked up again
  // after a reload — each shown in its own view, as a turn is.
  const works = useRunningGenerations();
  // This render's view and settings, for what runs later outside it — a
  // typed turn left waiting picked up again (hooks/usePendingTurn.js), a
  // reply that finished after its view was left, a timer.
  const live = useRef({});
  // A plan-quota 429 carries a curated reason already translated into the
  // account's language (free plan's daily count, or a paid plan's weekly /
  // 30-day quota) — show it instead of guessing "daily". A rate-limit 429
  // (it has Retry-After) gets our own localized text instead of its
  // Turkish-only detail. null for other errors.
  const quotaMessage = (e) => {
    if (e?.response?.status !== 429) return null;
    if (e.response.headers?.["retry-after"]) {
      return t("Çok hızlı gidiyorsun, birkaç saniye sonra tekrar dene.", "You're going a bit fast — try again in a few seconds.");
    }
    const detail = e.response.data?.detail;
    return (typeof detail === "string" && detail) || t("Kullanım sınırına ulaştın.", "You've reached your usage limit.");
  };
  // A picture or file still being made when the wait for it ended (a typed
  // turn's poll, a LunaWorks request sent again under its key): it turns up
  // once done — in this chat (here: a typed turn, whose thread is read again)
  // and in Ürettiklerim. Never "try again": that would make (and bill) it
  // twice.
  const toastStillMaking = (kind, { here = true } = {}) => {
    const tr = live.current.t || t;
    const image = makingGroup(kind) === "image";
    toast(here
      ? (image
        ? tr("Görselin hâlâ hazırlanıyor; birazdan burada ve Ürettiklerim'de görünecek. Yeniden istemene gerek yok 🌙",
          "Your image is still being made — it'll show up here and in your LunaWorks images shortly. No need to ask again 🌙")
        : tr("Dosyan hâlâ hazırlanıyor; birazdan burada ve Ürettiklerim'de görünecek. Yeniden istemene gerek yok 🌙",
          "Your file is still being made — it'll show up here and in your LunaWorks files shortly. No need to ask again 🌙"))
      : (image
        ? tr("Görselin hâlâ hazırlanıyor; bitince Ürettiklerim'de görünecek. Yeniden istemene gerek yok 🌙",
          "Your image is still being made — it'll show up in your LunaWorks images once done. No need to ask again 🌙")
        : tr("Dosyan hâlâ hazırlanıyor; bitince Ürettiklerim'de görünecek. Yeniden istemene gerek yok 🌙",
          "Your file is still being made — it'll show up in your LunaWorks files once done. No need to ask again 🌙")),
    { duration: 10000 });
  };
  live.current = { mode, conversationId, lang, mood, t, sending, making, quotaMessage };

  // Opens a view — a mode and, in it, a Sohbet (null: the mode's own
  // thread). A mode switch drops back to its default thread (the effect
  // below), unless a thread is asked for here.
  const nextConversation = useRef(null);
  const openView = (view) => {
    if (view.mode !== live.current.mode) {
      nextConversation.current = view.conversationId || null;
      setMode(view.mode);
    } else {
      setConversationId(view.conversationId || null);
    }
  };
  // Where a view is, in a toast: "o sohbette", "LunaWorks'te", "Arkadaş Modu'nda".
  const placeName = (tr, view) => (viewThread(view)
    ? tr("o sohbette", "in that chat")
    : view.mode === "work" ? tr("LunaWorks'te", "in LunaWorks") : tr("Arkadaş Modu'nda", "in Friend mode"));
  // Replies that finished while their view was closed (notices.readyElsewhere
  // below): { view, turn, answers }, put on screen once that view is open
  // again — by the toast's "Aç" or by going back to it — and then let go.
  // The mode's own thread opens on its greeting alone, so its reply was on
  // no screen at all, only in Sohbetlerim (health audit round 3); a Sohbet's
  // history holds it already (never added twice: mergeTurnAnswer).
  const unseen = useRef([]);
  const showUnseen = (view) => {
    const due = unseen.current.filter((u) => sameView(u.view, view));
    if (!due.length) return;
    unseen.current = unseen.current.filter((u) => !due.includes(u));
    setMessages((m) => due.reduce(
      (list, u) => (u.turn?.text ? mergeTurnAnswer(list, u.turn, u.answers) : addAnswers(list, u.answers)), m));
  };
  // A typed turn asked again after its reply never came (the toast below):
  // right here when its view is open and nothing else is being answered,
  // else its view opens with the words back in the composer.
  const resendTurn = (turn) => {
    const view = turnView(turn);
    if (sameView(live.current, view) && !live.current.sending) {
      handleSendRef.current(turn.text);
      return;
    }
    openView(view);
    setInput((cur) => cur || turn.text);
  };
  // What a turn's end says when its reply didn't simply land in the chat on
  // screen (hooks/usePendingTurn.js and the handlers below).
  const notices = {
    stillMaking: (kind, opts) => toastStillMaking(kind, opts),
    // The connection went while the server had the turn (its time ran out
    // with no answer at all): her answer is looked for in its thread once
    // the connection is back (hooks/usePendingTurn.js) — never "try again",
    // that would make (and bill) a file or a picture twice.
    connectionLost: (kind) => {
      const tr = live.current.t || t;
      const group = makingGroup(kind);
      toast(group === "image"
        ? tr("Bağlantı koptu; geri gelince görselini buraya getireceğim. Yeniden istemene gerek yok 🌙",
          "The connection dropped — I'll bring your image here once you're back. No need to ask again 🌙")
        : group === "doc"
        ? tr("Bağlantı koptu; geri gelince dosyanı buraya getireceğim. Yeniden istemene gerek yok 🌙",
          "The connection dropped — I'll bring your file here once you're back. No need to ask again 🌙")
        : tr("Bağlantı koptu; geri gelince cevabımı buraya getireceğim 🌙",
          "The connection dropped — I'll bring my reply here once you're back 🌙"),
      { duration: 10000 });
    },
    // The server had the turn, but its reply never came — it may have
    // failed (a build that outlived its deadline, a server restart): never
    // the promise that it turns up; sending it again is offered.
    maybeFailed: (turn) => {
      const tr = live.current.t || t;
      const group = makingGroup(turn.making);
      toast(group === "image"
        ? tr("Görselin gelmedi; çizilirken bir sorun olmuş olabilir. İstersen yeniden iste.",
          "Your image didn't arrive — something may have gone wrong while drawing it. You can ask again.")
        : group === "doc"
        ? tr("Dosyan gelmedi; hazırlanırken bir sorun olmuş olabilir. İstersen yeniden iste.",
          "Your file didn't arrive — something may have gone wrong while making it. You can ask again.")
        : tr("Cevabım sana ulaşmadı; bir sorun olmuş olabilir. İstersen yeniden gönder.",
          "My reply didn't reach you — something may have gone wrong. You can send it again."),
      { duration: 12000, action: { label: tr("Yeniden gönder", "Send again"), onClick: () => resendTurn(turn) } });
    },
    // A reply that finished after its chat or mode was left: kept in its own
    // thread (and, a picture or a file, in Ürettiklerim), not added to the
    // chat now on screen — the toast says where, and opens it; `answers`
    // (and the turn they answer) join that view once it is open (unseen).
    readyElsewhere: (view, kind, { turn = null, answers = [] } = {}) => {
      if (answers.length) unseen.current = [...unseen.current, { view, turn, answers }].slice(-UNSEEN_MAX);
      const tr = live.current.t || t;
      const group = makingGroup(kind);
      const where = placeName(tr, view);
      toast(group === "image"
        ? tr(`Görselin hazır — ${where} ve Ürettiklerim'de.`, `Your image is ready — ${where} and in your LunaWorks images.`)
        : group === "doc"
        ? tr(`Dosyan hazır — ${where} ve Ürettiklerim'de.`, `Your file is ready — ${where} and in your LunaWorks files.`)
        : tr(`Cevabım hazır — ${where}.`, `My reply is ready — ${where}.`),
      { duration: 10000, action: { label: tr("Aç", "Open"), onClick: () => openView(view) } });
    },
    // A typed turn left waiting in a view that isn't open — after a reload,
    // which opens the mode's own thread, one asked in a Sohbet (client
    // review, round 2: nothing said, the deck asked for again): the toast
    // says where; its "Aç" opens that view, which picks it up
    // (hooks/usePendingTurn.js). pending: still being made, as far as this
    // tab knows; else her answer is looked for there.
    waitingElsewhere: (view, kind, { pending = true } = {}) => {
      const tr = live.current.t || t;
      const group = makingGroup(kind);
      const where = placeName(tr, view);
      toast(!pending
        ? tr(`Son isteğin ${where} — cevabına orada bakalım.`, `Your last request is ${where} — let's look for its answer there.`)
        : group === "image"
        ? tr(`Görselin ${where} hazırlanıyor — yeniden istemene gerek yok.`, `Your image is being made ${where} — no need to ask again.`)
        : group === "doc"
        ? tr(`Dosyan ${where} hazırlanıyor — yeniden istemene gerek yok.`, `Your file is being made ${where} — no need to ask again.`)
        : tr(`Cevabım ${where} hazırlanıyor.`, `My reply is on its way ${where}.`),
      { duration: 12000, action: { label: tr("Aç", "Open"), onClick: () => openView(view) } });
    },
    // A turn picked up again that failed after all.
    failed: (e) => {
      if (isTermsRequired(e)) return; // (TermsGate is on its way — see useTermsRequired)
      const { t: tr, quotaMessage: quota } = live.current;
      toast.error(quota(e) || serverDetail(e) || tr(
        "🌙 Şu an sana cevap verirken küçük bir sorun yaşadım. Biraz sonra tekrar deneyelim.",
        "🌙 I ran into a small problem answering you just now. Let's try again in a bit."
      ), { duration: 6000 });
    },
  };
  // A reply of a media turn or a LunaWorks request: into the chat while its
  // view is open (once — a reloaded thread may hold it already), else the
  // toast above.
  const placeReply = (view, msg, kind) => {
    if (sameView(live.current, view)) setMessages((m) => addAnswers(m, [msg]));
    else notices.readyElsewhere(view, kind, { answers: [msg] });
  };
  // A turn being answered in this tab — and over.
  const beginTurn = ({ view, making: what = null, startedAt = Date.now(), stillWorking: still = false }) => {
    setSending(true);
    setMaking(what);
    setEtaS(null);
    setAgentStep(null);
    setStillWorking(still);
    setConnected(true);
    setTurnStartedAt(startedAt);
    setBusyView(view);
  };
  const endTurn = () => {
    setSending(false);
    setMaking(null);
    setEtaS(null);
    setAgentStep(null);
    setStillWorking(false);
    setConnected(true);
    setTurnStartedAt(null);
    setBusyView(null);
  };

  // The photos you sent show from blob: URLs of the files (runSendMedia):
  // each one is let go once no message on screen shows it — the chat was
  // switched or cleared — and all of them when the app goes (audit client-06).
  const previews = useRef(new Set());
  const previewUrl = (file) => {
    const url = URL.createObjectURL(file);
    previews.current.add(url);
    return url;
  };
  useEffect(() => {
    for (const url of staleObjectUrls(previews.current, messages)) {
      URL.revokeObjectURL(url);
      previews.current.delete(url);
    }
  }, [messages]);
  useEffect(() => {
    const kept = previews;
    return () => {
      kept.current.forEach((url) => URL.revokeObjectURL(url));
      kept.current.clear();
    };
  }, []);

  useEffect(() => {
    const down = () => setOffline(true);
    const up = () => setOffline(false);
    window.addEventListener("offline", down);
    window.addEventListener("online", up);
    return () => {
      window.removeEventListener("offline", down);
      window.removeEventListener("online", up);
    };
  }, []);

  useEffect(() => { localStorage.setItem("luna_mode", mode); }, [mode]);
  useEffect(() => {
    localStorage.setItem("luna_lang", lang);
    // (screen readers and the browser's own translate offer read the page's language)
    document.documentElement.lang = lang;
  }, [lang]);
  useEffect(() => {
    try {
      if (mood) {
        localStorage.setItem("luna_mood", mood);
        localStorage.setItem("luna_mood_day", dayKey());
      } else {
        localStorage.removeItem("luna_mood");
        localStorage.removeItem("luna_mood_day");
      }
    } catch {}
  }, [mood]);

  // The browser lands back here after an iyzico checkout (see
  // routers/subscription.py::billing_callback's RedirectResponse to
  // `${PUBLIC_APP_URL}/app?billing=success|failed|error`) — surface the
  // result once, then strip the query param so a refresh doesn't re-fire it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing");
    if (!billing) return;
    if (billing === "success") {
      toast.success(t("Ödeme alındı — Premium aktif! 🎉", "Payment received — Premium is active! 🎉"));
      setPremiumOpen(true);
    } else if (billing === "failed") {
      toast.error(t("Ödeme tamamlanamadı. Tekrar deneyebilirsin.", "Payment didn't go through. You can try again."));
      setPremiumOpen(true);
    } else {
      toast.error(t("Ödeme işlemiyle ilgili bir sorun oldu.", "Something went wrong with the payment."));
    }
    params.delete("billing");
    const newSearch = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-time terms check on mount (NOT in the messages effect below, which
  // re-runs on every mode switch). Accounts that never accepted the current
  // Kullanım Şartları version — older accounts, Google sign-ups, or everyone
  // after a terms update — get the TermsGate modal. A backend that doesn't
  // report terms_current_version yet shows nothing rather than a gate whose
  // "accept" could never stick.
  useEffect(() => {
    let cancelled = false;
    fetchProfile().then((p) => {
      if (cancelled) return;
      // The account's own language when it has one (lib/i18n.js
      // accountLanguage): a reply language other than TR/EN (only the
      // Settings picker sets one), or ANY language of an existing account
      // signing in on a new browser — a Turkish account on a German or
      // English browser stays Turkish (screens, replies, the mic) instead
      // of being moved to the browser's language and having that saved
      // over its own with the next message. Otherwise the browser's own
      // language, when Luna speaks it and it isn't TR/EN (seedReplyLang:
      // never over a choice), and a just-created account saves the
      // language it starts with right away.
      const own = accountLanguage(p);
      if (own) {
        setReplyLang(own.reply);
        if (own.ui) setLang(own.ui);
        // (the greeting already on screen was in the browser's language)
        setMessages((m) => (m.length === 1 && String(m[0]?.id || "").startsWith("welcome-")
          ? [buildWelcomeMessage(m[0].mode, own.ui || initialUiLang(), p?.name || "")] : m));
      } else {
        const seeded = seedReplyLang(replySeed);
        const startLang = seeded || getReplyLang() || initialUiLang();
        if (justCreatedHere(p) && startLang && startLang !== p?.profile?.language) {
          updateProfile({ language: startLang }).catch(() => {});
        }
      }
      if (!p?.terms_current_version) return;
      if (p.terms_accepted_version !== p.terms_current_version) setTermsProfile(p);
    }).catch(() => {
      if (!cancelled) seedReplyLang(replySeed);
    });
    return () => { cancelled = true; };
  }, [replySeed]); // (replySeed never changes: this runs once, on mount)

  // Indirection so onSpeechResult can call the CURRENT stop() without a
  // circular dependency (the hook below needs onSpeechResult as an input,
  // stop only exists once the hook itself has run).
  const sttStopRef = useRef(() => {});
  // Same indirection for handleSend: onSpeechResult is only recreated on
  // mode/lang changes, so calling handleSend directly would use a stale
  // copy (old conversationId, and a stale voice flag inside the call screen).
  const handleSendRef = useRef(() => {});

  // The voice call (hooks/useVoiceCall.js), reached through a ref so the
  // speech callbacks below don't need to be recreated with it.
  const callRef = useRef(null);

  // meta: { turnId } when the phrase came from server-side recognition (the
  // call's fallback engine) — the reply's voice then counts as the same turn.
  const onSpeechResult = useCallback((text, meta) => {
    // In a call, phrases are gathered into one turn (sent after a short
    // silence); in the chat composer a phrase is sent right away, as before.
    if (callRef.current?.handleFinal(text, meta)) return;
    sttStopRef.current();
    if (text) handleSendRef.current(text);
  }, []);
  const onSpeechEnd = useCallback((info) => callRef.current?.handleSpeechEnd(info), []);

  // It listens in the language you talk to Luna in — her reply language
  // (Settings, or the browser's own until that is kept), else the
  // interface's: German replies, German ears.
  const speech = useSpeechRecognition({ lang: getReplyLang() || replySeed || lang, onResult: onSpeechResult, onEnd: onSpeechEnd, autoRestart: false });
  const { listening, interim, supported, start, stop } = speech;
  sttStopRef.current = stop;

  const call = useVoiceCall({
    lang,
    mode,
    t,
    quotaMessage,
    speech,
    sending,
    sendTurn: (text, opts) => handleSendRef.current(text, { voice: true, turnId: opts?.turnId, signal: opts?.signal }),
    // An HD (speech-to-speech) call stores its turns in this thread on the
    // server; each finished one also shows in the chat behind the call
    // screen, as a classic call's turns do.
    conversationId,
    // (`actions`: what her tools did in the call — a reminder, a note — shown
    // as the reply's cards, even when she said nothing more about it.)
    onTranscriptTurn: ({ role, text, actions } = {}) => {
      const said = typeof text === "string" ? text.trim() : "";
      const cards = role === "assistant" && Array.isArray(actions) && actions.length ? actions : null;
      if ((!said && !cards) || (role !== "user" && role !== "assistant")) return;
      const id = `${role === "user" ? "u" : "l"}-rt-${randomId()}`;
      setMessages((m) => [...m, {
        id, role: role === "user" ? "user" : "luna", text: said, mode, ...(cards ? { actions: cards } : {}),
      }]);
    },
  });
  callRef.current = call;

  // The help card for a flagged message said in the call (see handleSend),
  // shown on the call screen until the call ends.
  const [callSafety, setCallSafety] = useState(null);
  useEffect(() => {
    if (!call.active) setCallSafety(null);
  }, [call.active]);

  // An AI request refused until the current Kullanım Şartları are accepted
  // (403 terms_required): the call screen closes and TermsGate shows — no
  // error toast. Once accepted the request can be sent again (a typed
  // message goes back into the composer — see handleSend).
  useTermsRequired({
    onRefused: () => {
      if (callRef.current?.active) callRef.current.close();
    },
    onGate: setTermsProfile,
  });

  useReminderAlerts({ lang });

  // The chat composer's mic has no screen of its own (the call shows its
  // errors itself): say what went wrong instead of silently doing nothing.
  const micErrorCtx = useRef({});
  micErrorCtx.current = { t, inCall: call.active };
  useEffect(() => {
    const { t: tr, inCall } = micErrorCtx.current;
    // (terms-required: TermsGate is on its way — see useTermsRequired)
    if (!speech.error || inCall || speech.error === "terms-required") return;
    const message = {
      "not-allowed": tr("Mikrofon izni gerekli. Tarayıcının site ayarlarından Luna'ya izin ver.", "Microphone access is needed. Allow it for Luna in your browser's site settings."),
      "audio-capture": tr("Mikrofon bulunamadı.", "No microphone found."),
      quota: tr("Sesli kullanım sınırına ulaştın; yazarak devam edebilirsin.", "You've reached your voice limit; you can keep going by text."),
    }[speech.error] || tr("Sesini alamadım, tekrar dener misin?", "I couldn't hear you. Could you try again?");
    toast.error(message);
  }, [speech.error]);

  // ---- A typed turn left waiting (lib/streamedTurn.js savePendingTurn) ----
  // Picked up again after a reload, or with the page back in view, while its
  // thread is on screen; a file still being made when its wait ended is
  // looked for in its thread a few more times (hooks/usePendingTurn.js).
  const moodToday = () => {
    try {
      return mood && localStorage.getItem("luna_mood_day") === dayKey() ? mood : null;
    } catch {
      return null;
    }
  };
  const pendingTurns = usePendingTurn({
    view: { mode, conversationId },
    sending,
    lang,
    mood: moodToday(),
    setMessages,
    beginTurn,
    endTurn,
    setStillWorking,
    setConnected,
    notices,
  });

  // Switching mode always drops back to that mode's default thread — a
  // specific Sohbet picked in Arkadaş Modu shouldn't still be "open" after
  // hopping to LunaWorks Modu and back. (openView may name the thread to
  // open instead: a toast's "Aç" for a reply that finished there.)
  useEffect(() => {
    setConversationId(nextConversation.current);
    nextConversation.current = null;
  }, [mode]);

  useEffect(() => {
    let cancelled = false;
    if (conversationId) {
      // A specific Sohbet (past or brand new) is open — show its real
      // transcript, the whole point of Sohbetler being an actual ChatGPT-
      // style history instead of one single ever-growing thread.
      fetchMessages(mode, conversationId).then((msgs) => {
        if (cancelled) return;
        setMessages(msgs.length ? msgs : [buildWelcomeMessage(mode, lang, userName)]);
        // (a reply that finished while this view was closed is in what just
        // loaded — its entry goes; never shown twice)
        showUnseen({ mode, conversationId });
        // (a turn left waiting here is looked for in what just loaded first)
        pendingTurns.resume(msgs);
      }).catch(() => {
        if (cancelled) return;
        setMessages([buildWelcomeMessage(mode, lang, userName)]);
        showUnseen({ mode, conversationId });
      });
      return () => { cancelled = true; };
    }
    // Arkadaş Modu and LunaWorks Modu each have their own separate default
    // thread — switching `mode` must re-fetch, not just keep showing
    // whichever mode's messages happened to load first.
    // Every time the app is opened (mount, reload, or a mode switch), Luna
    // greets first — a local-only, un-persisted bubble. Older saved history
    // stays on the server (still there for the backend/AI context) but is
    // deliberately NOT re-rendered here, so each visit starts on a clean,
    // uncluttered screen instead of an ever-growing wall of old messages.
    setMessages([buildWelcomeMessage(mode, lang, "")]);
    // (except what finished here while it was closed: under the greeting)
    showUnseen({ mode, conversationId: null });
    pendingTurns.resume();
    fetchProfile().then((profile) => {
      if (cancelled) return;
      const name = profile?.name || "";
      if (name) {
        setUserName(name);
        // (only the greeting: a turn picked up meanwhile stays)
        setMessages((m) => (String(m[0]?.id || "").startsWith("welcome-")
          ? [buildWelcomeMessage(mode, lang, name), ...m.slice(1)] : m));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, conversationId]);

  // voice: a turn spoken in the call (turnId, signal: see useVoiceCall).
  const handleSend = useCallback(async (text, { voice = false, turnId = null, signal = null } = {}) => {
    const content = (typeof text === "string" ? text : input).trim();
    if (!content || sending) return;
    if (!voice) setInput("");
    // The view the turn is asked in: its reply joins the screen only while
    // that is open — a mode switched or another Sohbet opened meanwhile gets
    // a toast that it is ready there instead (notices.readyElsewhere).
    const view = { mode, conversationId };
    const here = () => sameView(live.current, view);
    // (a typed "X hakkında sunum hazırla" / "bir kedi çiz": the waiting
    // bubble says a file / picture is being made — never in the call —
    // and, once the server says what it makes, that: onStatus below)
    let kind = voice ? null : pendingKind(content, features);
    // A typed turn, kept in this tab until it is answered: a reload
    // mid-build picks it up again under its key (hooks/usePendingTurn.js).
    // Its bubble carries the key, so it is known for this turn's own.
    const pending = voice ? null : { key: `text-${randomId()}`, text: content, mode, conversationId, making: kind, at: Date.now() };
    const optimistic = { id: pending ? `u-${pending.key}` : "u-" + Date.now(), role: "user", text: content, mode };
    setMessages((m) => [...m, optimistic]);
    beginTurn({ view, making: kind, startedAt: pending ? pending.at : Date.now() });
    // Which call (if any) this turn belongs to — a reply is only spoken in
    // the same call it was asked in.
    const callGen = callRef.current?.generation();
    // A typed turn's reply bubble, filled in as the reply streams (while its
    // view is open).
    const typed = voice ? null : streamingReply(setMessages, mode, { here, turn: pending });
    if (pending) savePendingTurn(pending);
    // (kept again as the turn goes: what the server says it makes, and that
    // it took the turn — a pick-up after a reload then waits for it to the
    // end, whatever the connection does: lib/streamedTurn.js pollTurn)
    let accepted = false;
    const keepPending = () => savePendingTurn({ ...pending, making: kind, ...(accepted ? { accepted: true } : {}) });
    // Whether the server said what the turn makes (assistant_status). It
    // always says so before a picture or a file is made, so her reply
    // starting without it means words, not a file: the guess above (a
    // "... raporu yaz" reads like a document) is let go — the waiting line, a
    // lost connection's toast and the page's "Leave site?" no longer promise
    // a file (client review, round 2).
    let statusSeen = false;
    const onStatus = ({ making: what, etaS: eta }) => {
      kind = what;
      statusSeen = true;
      setMaking(what);
      setEtaS(eta);
      if (pending) keepPending();
    };
    const onAccepted = () => {
      accepted = true;
      if (pending) keepPending();
    };
    const onDelta = (piece) => {
      if (!statusSeen && kind) {
        kind = null;
        setMaking(null);
        if (pending) keepPending();
      }
      typed.onDelta(piece);
    };
    // The server flags a message about self-harm or abuse (backend
    // services/safety.py): its reply then carries the help card.
    let safety = null;
    const onSafety = (s) => { safety = s; };
    try {
      // A mood picked on an earlier day no longer applies.
      const todaysMood = moodToday();
      // full: the whole turn comes back — a picture or file made in chat
      // (attachments) and the pages a searched reply used (sources) too.
      const args = { message: content, mode, lang, conversationId, mood: todaysMood, onSafety, full: true };
      // Both stream: a spoken turn so she starts talking at her first
      // sentence, a typed one so her reply shows up as it is written (and,
      // if the connection breaks off mid-reply, is fetched whole: recover —
      // while the server can still be on it, counted from `since`).
      // (onStillWorking: the stream broke off and the server is still on
      // it — the waiting bubble says so while the reply is asked for;
      // onConnection: a request got no answer at all — "Bağlantı bekleniyor…")
      const result = turnResult(voice
        ? await sendSpokenTurn(args, callRef, callGen, turnId, signal)
        : await sendStreamedTurn(args, { voice: false, idempotencyKey: pending.key, onDelta,
          recover: true, onStatus, onStep: setAgentStep, onAccepted, onStillWorking: () => setStillWorking(true), onConnection: setConnected,
          since: pending.at }));
      if (pending) clearPendingTurn(pending.key);
      // (messageId: the server's id of her reply — what its "Bildir" reports)
      const lunaMsg = {
        id: typed?.id || "l-" + Date.now(), role: "luna", text: result.reply, mode, messageId: result.messageId,
        ...(safety ? { safety } : {}), ...replyExtras(result),
      };
      if (typed) {
        if (!typed.finish(lunaMsg)) {
          notices.readyElsewhere(view, attachmentsKind(result.attachments), { turn: pending, answers: [lunaMsg] });
        }
      } else if (here()) {
        setMessages((m) => [...m, lunaMsg]);
      }
      if (voice && safety) setCallSafety(safety);
      // Chat text replies stay silent by default — Luna only speaks while
      // the dedicated voice-call screen is open (there the whole reply
      // finishes what streamed in, or is said from its start).
      if (voice && callRef.current?.isLive()) callRef.current.onReply(lunaMsg, callGen, turnId);
    } catch (e) {
      // A typed turn whose wait ended without its reply while the server had
      // it (lib/streamedTurn.js giveUpOutcome) is no ordinary failure: a file
      // or picture still being made within the server's deadline stays
      // pending and its thread is read again until her answer is there —
      // nothing suggests asking again, that would build and bill it twice
      // (audit client-01 / e2e-04); so does one the server took whose
      // connection went to the end (its time ran out with no answer at all:
      // it is brought once the connection is back — it used to end in the
      // "küçük bir sorun" toast after 40 s, inviting a second deck); one that
      // may have failed (a text reply that never came) offers to send it
      // again (hooks/usePendingTurn.js settle).
      if (pending && giveUpOutcome(e, kind)) {
        typed.drop();
        await pendingTurns.settle({ ...pending, making: kind }, e, kind);
        return;
      }
      if (pending) clearPendingTurn(pending.key);
      // Covers both a failed /api/chat call and a failed auth step inside
      // sendChat() (authHeaders() -> registerDevice(), which throws the
      // same way on a backend/network failure) — no backend detail, stack
      // trace, or system info reaches the user, just a short in-character
      // message. The optimistic user bubble above is intentionally left in
      // place (not removed) so nothing the user typed disappears, and
      // `finally` below always clears `sending` so the input stays usable
      // for an immediate retry. The one exception is a usage limit (429),
      // whose curated message tells the user when they can continue.
      // A flagged turn the server failed mid-reply (e.reply, see lib/api.js
      // streamChat) was closed with the caring crisis reply: that and the
      // help card take the partial text's place — never a "try again"
      // toast (launch audit 2026-10-03). In the call it goes into the chat
      // behind the call screen, and the card onto the call screen.
      if (e?.reply) {
        const kept = { id: typed?.id || "l-" + Date.now(), role: "luna", text: e.reply, mode, ...(safety ? { safety } : {}) };
        if (typed) {
          if (!typed.finish(kept)) notices.readyElsewhere(view, null, { turn: pending, answers: [kept] });
          return;
        }
        setMessages((m) => [...m, kept]);
        if (!callRef.current?.isLive()) return; // (the call is over: the reply and its card are in the chat)
        if (safety && callRef.current.generation() === callGen) setCallSafety(safety);
      }
      // A typed reply that failed after part of it showed (and couldn't be
      // fetched whole — see lib/streamedTurn.js) takes its partial text away.
      typed?.drop();
      // Refused until the current terms are accepted: TermsGate is on its
      // way (see useTermsRequired above; the call closes). The unanswered
      // bubble goes; a typed message goes back into the composer, ready to
      // send again once accepted.
      if (isTermsRequired(e)) {
        setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        if (!voice) setInput((cur) => cur || content);
        return;
      }
      // A turn spoken in the call is answered inside the call screen — and
      // one the call let go itself (hung up mid-answer) needs no message.
      if (voice && callRef.current?.isLive() && callRef.current.onTurnFailed(e, callGen)) return;
      if (voice && signal?.aborted) return;
      // A typed message the server refused as too long (413) was never
      // taken: it goes back into the composer (to shorten, or to send as a
      // file) instead of staying in the chat as if it had been sent.
      if (!voice && e?.response?.status === 413) {
        setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        setInput((cur) => cur || content);
      }
      // The server's own reason when it gives one (too long, not allowed,
      // a plan limit — lib/errorText.js), else the in-character message.
      // A plan's limit (the free plan's 30 messages a day, a paid plan's
      // quota — a 429 without Retry-After) offers the plans right there
      // while paid plans are on sale.
      const planLimit = e?.response?.status === 429 && !e.response.headers?.["retry-after"];
      toast.error(
        quotaMessage(e) || serverDetail(e) || t(
          "🌙 Şu an sana cevap verirken küçük bir sorun yaşadım. Biraz sonra tekrar deneyelim.",
          "🌙 I ran into a small problem answering you just now. Let's try again in a bit."
        ),
        planLimit && features.sales
          ? { duration: 10000, action: { label: t("Planları gör", "See plans"), onClick: () => setPremiumOpen(true) } }
          : { duration: 6000 }
      );
    } finally {
      endTurn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, sending, mode, lang, conversationId, mood, features]);
  handleSendRef.current = handleSend;

  // Same optimistic-bubble/error-handling shape as handleSend above, for an
  // image (png/jpg/webp/gif) or document (pdf/txt) instead of plain text —
  // see api.js::sendMedia. Images get a real local preview (msg.imageUrl,
  // an object URL — nothing is uploaded anywhere but Luna's own vision
  // call); documents just show their filename, matching what the backend
  // actually stores as this turn's message text. The turn belongs to the
  // view it was sent in — the open Sohbet keeps it (conversation_id; it used
  // to land in the mode's own thread, audit client-08) — and its reply joins
  // the screen only while that view is open (placeReply).
  const IMAGE_EXT = new Set(["png", "jpg", "jpeg", "webp", "gif"]);
  const runSendMedia = useCallback(async (file, caption) => {
    if (!file || sending) return;
    const view = { mode, conversationId };
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const isImage = IMAGE_EXT.has(ext);
    const optimistic = {
      id: "u-" + Date.now(),
      role: "user",
      text: caption?.trim() || (isImage ? "" : `📄 ${file.name}`),
      imageUrl: isImage ? previewUrl(file) : undefined,
      mode,
    };
    setMessages((m) => [...m, optimistic]);
    beginTurn({ view });
    try {
      // (a photo + "bunu anime yap" comes back with the new picture in
      // attachments — shown right in the reply)
      const result = turnResult(await sendMedia(file, caption?.trim(), mode, { full: true, conversationId }));
      const lunaMsg = { id: "l-" + Date.now(), role: "luna", text: result.reply, mode, messageId: result.messageId, ...replyExtras(result) };
      placeReply(view, lunaMsg, attachmentsKind(result.attachments));
    } catch (e) {
      if (isTermsRequired(e)) { // (TermsGate is on its way — see useTermsRequired)
        setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        return;
      }
      const status = e?.response?.status;
      // The server's own text first: a 413 is the file ("Dosya çok büyük
      // (max 15MB)"); a too-long caption is a 422 (code
      // "caption_too_long") that says how to shorten it.
      const msg = quotaMessage(e) || serverDetail(e) || (status === 415
        ? t("Bu dosya türünü okuyamıyorum (png, jpg, webp, gif, pdf, txt, csv, docx, xlsx, pptx, zip olmalı).", "I can't read this file type (must be png, jpg, webp, gif, pdf, txt, csv, docx, xlsx, pptx, or zip).")
        : status === 413
        ? t("Dosya çok büyük.", "That file is too large.")
        : t("🌙 Dosyayı işlerken küçük bir sorun yaşadım. Tekrar dener misin?", "🌙 I ran into a small problem with that file. Could you try again?"));
      toast.error(msg, { duration: 6000 });
    } finally {
      endTurn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending, mode, conversationId]);

  // Same shape as runSendMedia above, for up to 10 files sent together
  // in one turn (FriendPanel only calls this when more than one file is
  // pending — see its handleSendWithAttachment). Each image gets its own
  // optimistic bubble (so multiple images render the way a multi-image
  // message naturally would); non-image files collapse into one filename
  // summary bubble, carrying the caption if there were no images.
  const runSendMediaBatch = useCallback(async (files, caption) => {
    if (!files?.length || sending) return;
    const view = { mode, conversationId };
    const imageFiles = files.filter((f) => IMAGE_EXT.has((f.name.split(".").pop() || "").toLowerCase()));
    const docFiles = files.filter((f) => !imageFiles.includes(f));
    const trimmedCaption = caption?.trim() || "";
    const optimisticMsgs = imageFiles.map((f, i) => ({
      id: `u-${Date.now()}-${i}`,
      role: "user",
      text: i === 0 && !docFiles.length ? trimmedCaption : "",
      imageUrl: previewUrl(f),
      mode,
    }));
    if (docFiles.length) {
      optimisticMsgs.push({
        id: `u-${Date.now()}-docs`,
        role: "user",
        text: (trimmedCaption ? trimmedCaption + " — " : "") + docFiles.map((f) => `📄 ${f.name}`).join(", "),
        mode,
      });
    } else if (!imageFiles.length && trimmedCaption) {
      optimisticMsgs.push({ id: `u-${Date.now()}-caption`, role: "user", text: trimmedCaption, mode });
    }
    setMessages((m) => [...m, ...optimisticMsgs]);
    beginTurn({ view });
    try {
      const result = turnResult(await sendMediaBatch(files, trimmedCaption, mode, { full: true, conversationId }));
      const lunaMsg = { id: "l-" + Date.now(), role: "luna", text: result.reply, mode, messageId: result.messageId, ...replyExtras(result) };
      placeReply(view, lunaMsg, attachmentsKind(result.attachments));
    } catch (e) {
      if (isTermsRequired(e)) { // (TermsGate is on its way — see useTermsRequired)
        const sent = new Set(optimisticMsgs.map((x) => x.id));
        setMessages((m) => m.filter((x) => !sent.has(x.id)));
        return;
      }
      const status = e?.response?.status;
      // The server's own text first: a 413 may be too many files ("En fazla
      // 10 dosya ekleyebilirsin") or one too big; a too-long caption is a
      // 422 that says how to shorten it.
      const msg = quotaMessage(e) || serverDetail(e) || (status === 415
        ? t("Bu dosya türünü okuyamıyorum (png, jpg, webp, gif, pdf, txt, csv, docx, xlsx, pptx, zip olmalı).", "I can't read this file type (must be png, jpg, webp, gif, pdf, txt, csv, docx, xlsx, pptx, or zip).")
        : status === 413
        ? t("Dosyalardan biri çok büyük.", "One of the files is too large.")
        : t("🌙 Dosyaları işlerken küçük bir sorun yaşadım. Tekrar dener misin?", "🌙 I ran into a small problem with those files. Could you try again?"));
      toast.error(msg, { duration: 6000 });
    } finally {
      endTurn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending, mode, conversationId]);

  // ---- LunaWorks' wand: Luna DRAWS a picture or MAKES a file ----
  // Distinct from runSendMedia above, which sends an existing file for Luna
  // to look at. The user bubble is the request; the reply bubble carries the
  // picture itself (imageUrl) or a downloadable file card (fileUrl /
  // fileName — ChatMessage already renders both). Each request is a
  // generation (lib/generations.js): kept in this tab while it runs, so a
  // reload — or the page back in view — picks it up again under the same key
  // instead of building (and billing) it twice; its waiting bubble counts
  // from the first click and says it takes 1-4 minutes. It belongs to the
  // view it was asked in: the open Sohbet keeps it (conversation_id), and its
  // reply joins the chat only while that view is open — otherwise a toast
  // says where it is (audit client-08).
  // The generations shown in the chat: the wand's, and every one picked up
  // again (a panel's too — its panel may well be closed after a reload).
  const chatWorks = works.filter((w) => w.origin === "chat" || w.resumed);
  const imageWorking = chatWorks.some((w) => workKind(w) === "image");
  const docWorking = chatWorks.some((w) => workKind(w) !== "image");

  // Her reply: into the chat while its view is open, with the request's own
  // bubble when the screen lacks it; else the toast that says where it is.
  const placeWork = (entry, msg) => {
    const view = turnView(entry);
    if (!sameView(live.current, view)) {
      notices.readyElsewhere(view, workKind(entry), { turn: entry.text ? workTurn(entry) : null, answers: [msg] });
      return;
    }
    setMessages((m) => (entry.text ? mergeTurnAnswer(m, workTurn(entry), [msg]) : addAnswers(m, [msg])));
  };

  // A generation kept for later (still being made when its wait ended, or
  // the connection gone) is picked up again at its deadline too — not only
  // with the page back in view or online: by then the server is done with it
  // either way, and it is looked for in Ürettiklerim (lib/generations.js).
  const resumeTimers = useRef(new Set());
  const resumeAtDeadline = (entry) => {
    const timer = setTimeout(() => {
      resumeTimers.current.delete(timer);
      resumeGenerationsRef.current();
    }, Math.max(5000, generationDeadline(entry) - Date.now() + 1000));
    resumeTimers.current.add(timer);
  };

  const driveGeneration = async (entry, { resumed = false } = {}) => {
    const image = workKind(entry) === "image";
    try {
      // The backend actually finishes a picture in a few seconds, but
      // showing the result that fast reads as cheap/rushed for something
      // framed as "Luna drawing" — the visible "generating" state lasts at
      // least MIN_IMAGE_GEN_MS from the click.
      const result = await runGeneration(entry, { resumed, minMs: image ? MIN_IMAGE_GEN_MS : 0 });
      const tr = live.current.t || t;
      // (found in Ürettiklerim after its deadline — lib/generations.js — it
      // comes without a reply of its own)
      const text = typeof result.reply === "string" ? result.reply
        : image ? tr("İşte istediğin görsel! 🎨", "Here's the image you asked for! 🎨")
        : result.title ? tr(`"${result.title}" hazır! ✨`, `"${result.title}" is ready! ✨`)
        : tr("Dosyan hazır! ✨", "Your file is ready! ✨");
      const base = { id: `l-${randomId()}`, role: "luna", text, mode: entry.mode, messageId: result.messageId };
      // (imageRef / fileRef: a replay whose bytes weren't in the answer —
      // fetched by the message's id when shown, lib/api.js sendGenerate)
      if (image) {
        placeWork(entry, { ...base, imageUrl: result.imageUrl, ...(result.imageRef ? { imageRef: result.imageRef } : {}) });
        return;
      }
      // (the extension is the file's own type — lib/files.js — its route's
      // when it says none)
      const fileName = downloadName(fileTitle(result.title), result.mimeType, ROUTE_EXT[entry.route] || "");
      placeWork(entry, { ...base, fileUrl: result.fileUrl, fileName, ...(result.fileRef ? { fileRef: result.fileRef } : {}) });
    } catch (e) {
      if (isTermsRequired(e)) { // (TermsGate is on its way — see useTermsRequired)
        setMessages((m) => m.filter((x) => x.id !== `u-${entry.key}`));
        return;
      }
      // (picked up again, and kept for later the same way as before: that
      // was said already — not again on each return to the page)
      if (e?.told) return;
      // Still being made when the wait for it ended (lib/api.js sends it
      // again under its key while the connection keeps going): it lands in
      // Ürettiklerim — asking again would make (and bill) it twice.
      if (e?.stillWorking) {
        toastStillMaking(workKind(entry), { here: false });
        resumeAtDeadline(entry);
        return;
      }
      const { t: tr, quotaMessage: quota } = live.current;
      // No answer at all for a minute (a phone locked, offline): it may be
      // being made all the same — kept, and picked up again once the page is
      // back in view or online, or at its deadline (lib/generations.js).
      if (e?.connectionLost) {
        toast(image
          ? tr("Bağlantı koptu; geri gelince görselini kaldığı yerden alacağım. Yeniden istemene gerek yok 🌙",
            "The connection dropped — I'll pick your image up where it left off once you're back. No need to ask again 🌙")
          : tr("Bağlantı koptu; geri gelince dosyanı kaldığı yerden alacağım. Yeniden istemene gerek yok 🌙",
            "The connection dropped — I'll pick your file up where it left off once you're back. No need to ask again 🌙"),
        { duration: 10000 });
        resumeAtDeadline(entry);
        return;
      }
      // Past its deadline and not in Ürettiklerim: it may have failed (a build
      // that ran out of time, a restart) — never "still being made"; a new
      // request is offered, never sent by itself.
      if (e?.mayHaveFailed) {
        toast(image
          ? tr("Görselin gelmedi; çizilirken bir sorun olmuş olabilir. Ürettiklerim'de yoksa yeniden isteyebilirsin.",
            "Your image didn't arrive — something may have gone wrong while drawing it. If it isn't in your LunaWorks images, you can ask again.")
          : tr("Dosyan gelmedi; hazırlanırken bir sorun olmuş olabilir. Ürettiklerim'de yoksa yeniden isteyebilirsin.",
            "Your file didn't arrive — something may have gone wrong while making it. If it isn't in your LunaWorks files, you can ask again."),
        { duration: 12000, action: { label: tr("Yeniden iste", "Ask again"), onClick: () => askAgain(entry) } });
        return;
      }
      // A picture refused under the content rules (422 "image_refused" with
      // the reason and the strikes left; 502 from an older server) or
      // picture making paused after repeated refusals (403) must say so —
      // "tekrar dener misin?" there only leads into (or prolongs) the pause.
      // So do "very busy" (503) and a failed picture's or file's own reason
      // (500): the server's sentence as it came.
      const msg = quota(e) || serverDetail(e, image ? [403, 413, 422, 429, 500, 502, 503] : GENERATE_DETAIL_STATUSES)
        || (image
          ? tr("🌙 Görseli çizerken küçük bir sorun yaşadım. Tekrar dener misin?", "🌙 I ran into a small problem drawing that. Could you try again?")
          : tr("🌙 Oluştururken küçük bir sorun yaşadım. Tekrar dener misin?", "🌙 I ran into a small problem generating that. Could you try again?"));
      toast.error(msg, { duration: image ? 8000 : 6000 });
    }
  };

  // A generation that did not turn up, asked for anew from its toast: the
  // same request under a new key, in its own view (its bubble goes in when
  // that view is open) — one picture and one file at a time, as from the wand.
  const askAgain = (entry) => {
    const kind = workKind(entry);
    const busyNow = runningGenerations().some((w) => (w.origin === "chat" || w.resumed)
      && (workKind(w) === "image") === (kind === "image"));
    if (busyNow) {
      toastWorkBusy(kind);
      return;
    }
    const fresh = newGeneration({
      route: entry.route, fields: entry.fields, mode: entry.mode, conversationId: entry.conversationId,
      making: entry.making, text: entry.text, origin: "chat",
    });
    if (fresh.text && sameView(live.current, turnView(fresh))) {
      setMessages((m) => [...m, { id: `u-${fresh.key}`, role: "user", text: fresh.text, mode: fresh.mode }]);
    }
    driveGeneration(fresh);
  };

  // A new wand request, from the view on screen.
  const startWork = ({ route, fields, making: what, text }) => {
    const entry = newGeneration({ route, fields, mode, conversationId, making: what, text, origin: "chat" });
    setMessages((m) => [...m, { id: `u-${entry.key}`, role: "user", text, mode }]);
    driveGeneration(entry);
  };

  // One picture and one file at a time from the wand: the waiting bubble
  // stops a second one in its own chat (FriendPanel), and this says why
  // when the first is being made in another chat.
  const toastWorkBusy = (kind) => {
    toast(makingGroup(kind) === "image"
      ? t("Bir görselin zaten çiziliyor; bitince yenisini isteyebilirsin 🌙", "An image of yours is already being drawn — you can ask for the next one once it's done 🌙")
      : t("Bir dosyan zaten hazırlanıyor; bitince yenisini isteyebilirsin 🌙", "A file of yours is already being made — you can ask for the next one once it's done 🌙"),
    { duration: 6000 });
  };

  // ...and one picked up again: after a reload (once the chat is on screen —
  // this effect comes after the one that opens it), with the page back in
  // view or back online, and at its deadline (resumeAtDeadline). One already
  // being waited on here is left alone (lib/generations.js pickUpKept).
  const resumeGenerations = () => pickUpKept((entry) => {
    if (entry.text && sameView(live.current, turnView(entry))) {
      setMessages((m) => mergeTurnAnswer(m, workTurn(entry), []));
    }
    driveGeneration(entry, { resumed: true });
  });
  const resumeGenerationsRef = useRef(resumeGenerations);
  resumeGenerationsRef.current = resumeGenerations;
  useEffect(() => {
    resumeGenerationsRef.current();
    const onShow = () => {
      if (document.visibilityState === "visible") resumeGenerationsRef.current();
    };
    const onOnline = () => resumeGenerationsRef.current();
    // Closing or reloading the page while a picture or file is being made
    // asks first (the browser's own "Leave site?"): it would still be picked
    // up again, but nothing on screen would say so until it is.
    const onLeave = (e) => {
      const { sending: busy, making: what } = live.current;
      if (holdPageOnLeave(e, busy && Boolean(makingGroup(what)))) return LEAVE_PROMPT;
      return undefined;
    };
    const timers = resumeTimers.current;
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("online", onOnline);
    window.addEventListener("beforeunload", onLeave);
    return () => {
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("beforeunload", onLeave);
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  // What FriendPanel calls: true when the send is taken. It clears the
  // composer — the text, the attached files, the wand mode — only then: a
  // send while another is still being answered is refused, and nothing typed
  // or attached is lost (audit client-03).
  const handleSendMedia = (file, caption) => {
    if (!file || sending) return false;
    runSendMedia(file, caption);
    return true;
  };
  const handleSendMediaBatch = (files, caption) => {
    if (!files?.length || sending) return false;
    runSendMediaBatch(files, caption);
    return true;
  };
  const handleGenerateImage = (prompt) => {
    if (!prompt) return false;
    if (imageWorking) {
      toastWorkBusy("image");
      return false;
    }
    startWork({ route: GENERATE_ROUTES.image, fields: { prompt }, making: "image", text: `🎨 ${prompt}` });
    return true;
  };
  const handleGenerateDoc = (kind, prompt) => {
    const cfg = DOC_GEN_CONFIG[kind];
    if (!cfg || !prompt) return false;
    if (docWorking) {
      toastWorkBusy(cfg.making);
      return false;
    }
    startWork({ route: cfg.route, fields: { raw_data: prompt, title: "" }, making: cfg.making, text: prompt });
    return true;
  };

  // The chat composer's mic (the call has its own controls).
  const toggleMic = () => {
    if (!supported) {
      toast.error(t("Tarayıcı sesli girişi desteklemiyor", "Voice input not supported here"));
      return;
    }
    listening ? stop() : start();
  };

  const handleClear = async () => {
    await clearMessages(mode, conversationId);
    setMessages([]);
    setSettingsOpen(false);
    toast.success(t("Sohbet temizlendi", "Chat cleared"));
  };

  const handleClearAllMemories = async () => {
    await clearMemories();
    setSettingsOpen(false);
    toast.success(t("Tüm anılar silindi", "All memories cleared"));
  };

  // "Bildir" under one of Luna's messages (components/ChatMessage.jsx) —
  // stable, so the memoized bubbles don't re-render for it.
  const openReport = useCallback((msg) => setReportTarget(msg), []);
  // The report dialog's send (components/ReportDialog.jsx toasts the
  // outcome): her reply's server id — the one its turn came back with, or a
  // reloaded message's own — else, for a turn said in an HD call (the server
  // stored it itself and never said its id), her latest message with that
  // text in this thread as the server has it.
  const submitReport = async ({ category, note }) => {
    const msg = reportTarget;
    const id = serverMessageId(msg) || findInHistory(msg, await fetchMessages(mode, conversationId));
    if (!id) throw new Error("report: message not found on the server");
    await reportMessage(id, { category, note });
  };

  // What the chat on screen shows of the work going on (FriendPanel): the
  // turn being answered when it belongs to this view, else a generation of
  // this view; one of another view only says so in the status line.
  const here = { mode, conversationId };
  const turnHere = sending && (!busyView || sameView(busyView, here));
  const workHere = turnHere ? null : chatWorks.find((w) => sameView(w, here)) || null;
  const shownKind = turnHere ? making : workKind(workHere);
  const worksThere = chatWorks.filter((w) => !sameView(w, here));
  const shownGroup = turnHere || workHere ? makingGroup(shownKind) : null;

  if (booting) {
    return <BootSequence lang={lang} onDone={() => {
      try { localStorage.setItem("luna_booted", "1"); } catch { /* shows again next time */ }
      setBooting(false);
    }} />;
  }

  return (
    <div className="relative h-app overflow-hidden flex bg-[#05040c]" data-testid="luna-app">
      <div className="pointer-events-none fixed inset-0 z-0 bg-cover bg-bottom"
        style={{ backgroundImage: `url(${lunaBg})` }} />
      <div className="pointer-events-none fixed inset-0 z-0"
        style={{ background: "linear-gradient(180deg, rgba(5,4,12,0.55) 0%, rgba(5,4,12,0.75) 55%, rgba(5,4,12,0.92) 100%)" }} />
      <div className="pointer-events-none fixed inset-0 z-0"
        style={{ background: "radial-gradient(50% 35% at 20% 0%, rgba(217,70,239,0.14), transparent 70%)" }} />

      {/* inert while the full-screen call is open: keyboard focus and screen
          readers stay inside the call instead of reaching hidden controls. */}
      <div className="contents" inert={call.active}>
        <Sidebar
          mode={mode}
          lang={lang}
          active={mode}
          onNavigate={(key) => setMode(key === "work" ? "work" : "friend")}
          onOpenMemories={() => setMemoriesOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenPanel={setOpenPanel}
          onOpenPremium={() => setPremiumOpen(true)}
          onOpenConversations={() => setConversationsOpen(true)}
          mobileOpen={mobileMenuOpen}
          onCloseMobile={() => setMobileMenuOpen(false)}
        />
      </div>

      <div className="relative z-10 flex-1 min-w-0 h-app overflow-hidden flex flex-col px-4 sm:px-8 py-4 gap-3" inert={call.active}>
        <FriendPanel
          lang={lang}
          messages={messages}
          sending={sending}
          // (the fallback recognizer is still turning your words into text)
          listening={listening || speech.transcribing}
          interim={interim}
          input={input}
          setInput={setInput}
          onSend={handleSend}
          onSendMedia={handleSendMedia}
          onSendMediaBatch={handleSendMediaBatch}
          onGenerateImage={handleGenerateImage}
          // (a wand request, or a typed turn making a picture / a file in
          // chat — the waiting bubble's "Görsel çiziliyor..." / "Sunum
          // hazırlanıyor...")
          generatingImage={shownGroup === "image"}
          onGenerateDoc={handleGenerateDoc}
          generatingDoc={shownGroup === "doc"}
          making={shownKind}
          etaS={turnHere ? etaS : null}
          agentStep={turnHere ? agentStep : null}
          stillWorking={turnHere && stillWorking}
          startedAt={turnHere ? turnStartedAt : workHere ? workHere.startedAt : null}
          waitingForNetwork={!connected || offline}
          waitingHere={!sending || turnHere || Boolean(workHere)}
          work={workHere ? "here" : worksThere.length ? "elsewhere" : null}
          // (what the one of another chat makes, for its status line; and a
          // turn being answered in another chat, which that line says first)
          workElsewhere={worksThere.length ? (worksThere.every((w) => workKind(w) === "image") ? "image" : "doc") : null}
          turnElsewhere={sending && !turnHere}
          onToggleMic={toggleMic}
          onOpenCall={call.open}
          workMode={mode === "work"}
          onOpenMobileMenu={() => setMobileMenuOpen(true)}
          onReport={openReport}
        />
      </div>

      {settingsOpen && (
        <PanelSlot lang={lang} onClose={() => setSettingsOpen(false)}>
        <SettingsPanel
          lang={lang}
          setLang={setLang}
          onClearChat={handleClear}
          onClearMemories={handleClearAllMemories}
          onClose={() => setSettingsOpen(false)}
        />
        </PanelSlot>
      )}

      {memoriesOpen && (
        <PanelSlot lang={lang} onClose={() => setMemoriesOpen(false)}>
          <MemoryPanel lang={lang} onClose={() => setMemoriesOpen(false)} />
        </PanelSlot>
      )}

      {conversationsOpen && (
        <ConversationsPanel
          lang={lang}
          mode={mode}
          activeConversationId={conversationId}
          onSelect={setConversationId}
          onClose={() => setConversationsOpen(false)}
        />
      )}

      {openPanel === "notes" && <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}><NotesPanel lang={lang} onClose={() => setOpenPanel(null)} /></PanelSlot>}
      {openPanel === "goals" && <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}><GoalsPanel lang={lang} onClose={() => setOpenPanel(null)} /></PanelSlot>}
      {openPanel === "alarms" && <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}><RemindersPanel lang={lang} onClose={() => setOpenPanel(null)} /></PanelSlot>}
      {openPanel === "journal" && <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}><JournalPanel lang={lang} onClose={() => setOpenPanel(null)} /></PanelSlot>}
      {openPanel === "hobbies" && <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}><HobbiesPanel lang={lang} onClose={() => setOpenPanel(null)} /></PanelSlot>}
      {openPanel === "mood" && <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}><MoodPanel lang={lang} mood={mood} setMood={setMood} onClose={() => setOpenPanel(null)} /></PanelSlot>}
      {openPanel === "day-info" && <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}><DayInfoPanel lang={lang} onClose={() => setOpenPanel(null)} /></PanelSlot>}
      {openPanel === "usage" && (
        <PanelSlot lang={lang} onClose={() => setOpenPanel(null)}>
          <UsagePanel lang={lang} onClose={() => setOpenPanel(null)}
            onOpenPremium={() => { setOpenPanel(null); setPremiumOpen(true); }} />
        </PanelSlot>
      )}
      {openPanel === "work-images" && <ImageGalleryPanel lang={lang} onClose={() => setOpenPanel(null)} />}
      {openPanel === "work-pdf" && <DocGeneratorPanel kind="pdf" lang={lang} mode={mode} onClose={() => setOpenPanel(null)} />}
      {openPanel === "work-excel" && <DocGeneratorPanel kind="excel" lang={lang} mode={mode} onClose={() => setOpenPanel(null)} />}
      {openPanel === "work-word" && <DocGeneratorPanel kind="word" lang={lang} mode={mode} onClose={() => setOpenPanel(null)} />}
      {openPanel === "work-ppt" && <DocGeneratorPanel kind="ppt" lang={lang} mode={mode} onClose={() => setOpenPanel(null)} />}
      {openPanel === "work-table" && <DocGeneratorPanel kind="table" lang={lang} mode={mode} onClose={() => setOpenPanel(null)} />}
      {openPanel === "work-chart" && <DocGeneratorPanel kind="chart" lang={lang} mode={mode} onClose={() => setOpenPanel(null)} />}
      {premiumOpen && <PremiumPanel lang={lang} onClose={() => setPremiumOpen(false)} />}

      {call.active && <VoiceCallModal lang={lang} {...call.modalProps} safety={callSafety} />}

      {reportTarget && (
        <ReportDialog lang={lang} onSubmit={submitReport} onClose={() => setReportTarget(null)} />
      )}

      {termsProfile && (
        <TermsGate
          lang={lang}
          currentVersion={termsProfile.terms_current_version}
          initialConsent={!!termsProfile.special_data_consent}
          onAccepted={() => setTermsProfile(null)}
        />
      )}
    </div>
  );
}
